import { useMemo } from "react";

type HtmlProps = {
  contentEditable?: boolean | "true" | "false" | "inherit";
  suppressContentEditableWarning?: boolean;
  tabIndex?: number;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseOver?: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  style?: React.CSSProperties;
  role?: string;
  href?: string;
  download?: string | boolean;
  target?: string;
};

// attributes accepted on native elements
const htmlProps: (keyof HtmlProps)[] = [
  "contentEditable",
  "suppressContentEditableWarning",
  "tabIndex",
  "onKeyPress",
  "onClick",
  "onMouseDown",
  "onMouseOver",
  "onMouseEnter",
  "onMouseLeave",
  "onFocus",
  "onBlur",
  "style",
  "role",
  "href",
  "download",
  "target",
];

/**
 * Hook to filter and extract only HTML-compatible props from a props object
 *
 * @param props - Props object to filter
 */
const useHtmlProps = (props: Record<string, any>): Partial<HtmlProps> => {
  const propsToPass = useMemo(
    () =>
      htmlProps
        .filter((att) => Object.prototype.hasOwnProperty.call(props, att))
        .reduce(
          (acc, elem) => {
            acc[elem] = props[elem];
            return acc;
          },
          {} as Record<string, any>,
        ) as Partial<HtmlProps>,
    // Using props object directly in dependency array causes infinite loop due to new reference on every render
    // Instead, monitor only actual value changes by using concatenated string of values
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [htmlProps.map((key) => props[key]).join(",")],
  );

  return propsToPass;
};

export default useHtmlProps;
